import { useEffect, useState } from "react";

import { DocumentNode } from "@apollo/client";
import { Callout, Colors, Intent, Tab, Tabs } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { useLocation } from "react-router";

import { Box, BoxProps } from "@/components/layout/flexbox";
import { breakpoints } from "@/components/layout/Media";
import { getComponentDetailsPaths } from "@/components/pages_logged_in/program/components/component-utils";
import {
  ConfigureTabIndexByType,
  getComponentTabList,
  ICON_FULL_CIRCLE,
  ICON_TICK_CIRCLE,
} from "@/components/pages_logged_in/program/components/config/ProgramComponentTabs";
import { validateComponentDate } from "@/components/pages_logged_in/program/components/validate-component-date";
import { ALL_PARTICIPANTS_OPTION } from "@/components/pages_logged_in/program/constants";
import { LoadingOverlay } from "@/components/pieces/loading/LoadingOverlay";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { BP_NS } from "@/css/constants";
import {
  ChildProgramComponentInput,
  ComponentStatus,
  ComponentTypeEnum,
  ProgramComponentInput,
  ProgramComponentsQueryDocument,
  ProgramQueryDocument,
  ProgramStatus,
  useProgramComponentQueryLazyQuery,
  useSaveProgramComponentMutation,
  useSurveyTemplatesQueryLazyQuery,
} from "@/graphql";
import {
  ComponentInput,
  ConfigureInput,
  MainComponent,
  NextInputProps,
} from "@/types/programs";
import { ensureNonNullArray } from "@/utils/array";

import {
  getIntroSessionInput,
  getMeetingSessionInput,
  mapComponentToInput,
} from "./utils";

const ErrorBoxCSS: BoxProps["css"] = {
  position: "sticky",
  top: 0,
  background: Colors.WHITE,
  zIndex: 9,
};

const isComponentTypeSetupComplete = (input: ConfigureInput): boolean => {
  switch (input?.type) {
    case ComponentTypeEnum.Survey:
    case ComponentTypeEnum.Video:
    case ComponentTypeEnum.Article:
    case ComponentTypeEnum.ActionItem:
      return !!input?.formFields?.length && !!input?.targetDueDate;

    case ComponentTypeEnum.Announcement:
      return !!input?.body;

    case ComponentTypeEnum.GroupMeeting:
      return !!input?.formFields?.length && !!input?.formFields[0]?.link;

    case ComponentTypeEnum.OneOneCoachingSession:
      return !!input.productId;

    case ComponentTypeEnum.OneOneCoachingMaster:
      return !!input?.specialty;

    default:
      return true;
  }
};

const isBaseSetupComplete = (input: ConfigureInput): boolean => {
  if (input?.type === ComponentTypeEnum.OneOneCoachingIntro) {
    return !!input?.targetStartDate && !!input?.targetEndDate;
  }
  return !!input?.name && !!input?.targetStartDate && !!input?.targetEndDate;
};

const isParticipantSetupComplete = (input: ConfigureInput): boolean => {
  return (
    !!input?.participants?.length ||
    input?.forAllParticipants ||
    !!input?.pendingParticipantEmails?.length
  );
};

const isChildCoachingComponent = (type?: ComponentTypeEnum | null): boolean => {
  return (
    !!type &&
    [
      ComponentTypeEnum.OneOneCoachingIntro,
      ComponentTypeEnum.OneOneCoachingSession,
    ].includes(type)
  );
};

const isComponentSetupComplete = (input: ConfigureInput): boolean => {
  const isTypecSetupValid = isComponentTypeSetupComplete(input);
  const isBaseSetupValid = isBaseSetupComplete(input);

  if (isChildCoachingComponent(input?.type)) {
    return isBaseSetupValid && isTypecSetupValid;
  }

  return (
    isTypecSetupValid &&
    isBaseSetupValid &&
    isParticipantSetupComplete(input) &&
    !!input?.type
  );
};

const buildComponentPayloadForSaved = (
  input: ConfigureInput,
): ProgramComponentInput => {
  const isSetupComplete = isComponentSetupComplete(input);
  const payloadForSaved = {
    ...input,
    isSetupComplete: isSetupComplete,
  };

  if (
    input.type &&
    [
      ComponentTypeEnum.Survey,
      ComponentTypeEnum.ActionItem,
      ComponentTypeEnum.Video,
      ComponentTypeEnum.Article,
      ComponentTypeEnum.GroupMeeting,
    ].includes(input.type)
  ) {
    const formFields = ensureNonNullArray(input.formFields);

    // Format the input before saved
    const newFormFields = formFields.map((q) => {
      return {
        ...q,
        id: q.id && q.id.indexOf("new") > -1 ? undefined : q.id,
      };
    });
    payloadForSaved.formFields = newFormFields;
  }

  delete payloadForSaved.roleplayScenarios;
  delete payloadForSaved.status;
  return payloadForSaved;
};

const getIntroSessionPayload = (
  introSessionInput?: ChildProgramComponentInput,
) => {
  if (!introSessionInput) return null;

  const isValidId = introSessionInput?.id?.indexOf("new") === -1;
  return {
    id: isValidId ? introSessionInput?.id : undefined,
    targetStartDate: introSessionInput?.targetStartDate,
    targetEndDate: introSessionInput?.targetEndDate,
    introSessionCount: introSessionInput?.introSessionCount,
    participantGuidance: introSessionInput?.participantGuidance,
    type: introSessionInput?.type,
    isSetupComplete: isComponentSetupComplete(
      introSessionInput as ConfigureInput,
    ),
  };
};

const getMeetingSessionPayload = (input?: ChildProgramComponentInput[]) => {
  if (!input) return null;

  return input.map((session) => {
    const isValidId = session?.id?.indexOf("new") === -1;
    return {
      id: isValidId ? session?.id : undefined,
      name: session?.name,
      targetStartDate: session?.targetStartDate,
      targetEndDate: session?.targetEndDate,
      participantGuidance: session?.participantGuidance,
      productId: session?.productId,
      isSetupComplete: isComponentSetupComplete(session as ConfigureInput),
      optional: session?.optional,
    };
  });
};

const INITIAL_INPUT_STATE: ConfigureInput = {
  forAllParticipants: true,
  participants: [ALL_PARTICIPANTS_OPTION.value],
  pendingParticipantEmails: [],
  isReleaseDependentComponents: false,
  isSendMeetingUpdateEmail: false,
  visibleUpcomingQuestions: false,
  requiredSendLaunchEmail: true,
  requiredSendDueEmail: true,
  requiredSendPastDueEmail: true,
  status: ComponentStatus.InDesign,
  formFields: [],
};

const DEFAULT_NAMES: Partial<Record<ComponentTypeEnum, string>> = {
  [ComponentTypeEnum.ActionItem]: "Action Items",
  [ComponentTypeEnum.AiRoleplay]: "AI Roleplay",
  [ComponentTypeEnum.Video]: "Watch Video",
  [ComponentTypeEnum.Article]: "Read Article",
};

interface AddProgramComponentDialogProps {
  open: boolean;
  onClose?: (componentId?: string) => void;
  programId: string;
  programStatus?: ProgramStatus | null;
  targetLaunchDate?: string | null;
  componentId?: string;
  componentType?: ComponentTypeEnum;
  extraOnSavedRefetchs?: {
    query: DocumentNode;
    variables: ExplicitAny;
  }[];
  redirect?: string;
  redirectByComponentType?: boolean;
}

export function AddProgramComponentDialog({
  open,
  onClose,
  programId,
  programStatus,
  targetLaunchDate,
  componentId,
  componentType,
  extraOnSavedRefetchs = [],
  redirect = "",
  redirectByComponentType = false,
}: AddProgramComponentDialogProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [programComponent, setProgramComponent] = useState<MainComponent>();
  const [input, setInput] = useState<ConfigureInput>(INITIAL_INPUT_STATE);
  const location = useLocation();

  const [introSessionInput, setIntroSessionInput] =
    useState<ChildProgramComponentInput>();

  const [meetingSessionInput, setMeetingSessionInput] = useState<
    ChildProgramComponentInput[]
  >([]);

  const [fetchComponent, { loading }] = useProgramComponentQueryLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.programComponent) {
        const component = data?.programComponent;
        if (component) {
          const inputObj = mapComponentToInput(component);
          const introSessionInput = getIntroSessionInput(component);
          const meetingSessionInput = getMeetingSessionInput(component);
          setInput(inputObj);
          setIntroSessionInput(introSessionInput);
          setMeetingSessionInput(meetingSessionInput);
          setProgramComponent(component);
        }
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    },
    onError: (err) => {
      setErrorMessage(err.message);
    },
  });

  const tabList = getComponentTabList(input.type, componentId);

  const [selectedTabId, setSelectedTabId] = useState(tabList?.[0]?.key);

  const handleNextTab = () => {
    const nextTab = tabList?.find((item) => item?.key === selectedTabId + 1);
    if (nextTab) {
      setSelectedTabId(nextTab?.key);
    }
  };

  const [fetchSurveyTemplates, { data: surveyTemplateData }] =
    useSurveyTemplatesQueryLazyQuery();

  const onSavedRefetchQueries = [
    {
      query: ProgramQueryDocument,
      variables: {
        programId,
      },
    },
    {
      query: ProgramComponentsQueryDocument,
      variables: {
        programId,
      },
    },
  ].concat(extraOnSavedRefetchs);

  const [saveProgramComponent, { loading: saving }] =
    useSaveProgramComponentMutation({
      refetchQueries: onSavedRefetchQueries,
      onCompleted: (data) => {
        const { ok, msg } = data?.saveProgramComponent || {};
        if (ok) {
          const { programComponent } = data?.saveProgramComponent || {};
          if (programComponent) {
            fetchComponent({
              variables: {
                programId,
                programComponentId: programComponent?.id,
              },
            });
            handleNextTab();
          } else {
            setErrorMessage("Cannot save program component");
          }
        } else {
          setErrorMessage(msg || "Cannot save program component");
        }
      },
      onError: (err) => {
        setErrorMessage(err.message);
      },
    });

  const handleTabChange = (newTabId: number) => {
    setSelectedTabId(newTabId);
  };

  const validateInput = (input: ComponentInput, tabId: number) => {
    const tabShouldValidates =
      ConfigureTabIndexByType[input.type || ComponentTypeEnum.Survey];
    if (tabShouldValidates && !tabShouldValidates.includes(tabId)) return true;

    const validateDate = validateComponentDate(input, programComponent);
    if (!validateDate.valid) {
      setErrorMessage(validateDate.message);
      return false;
    }

    return validateDate.valid;
  };

  const handleOnNext = ({
    value,
    isSave,
    isNext,
    skipValidation,
    introSessionValue,
    meetingSessionValue,
  }: NextInputProps) => {
    setErrorMessage(null);

    const newInput = { ...input, ...value };

    if (!skipValidation && !validateInput(newInput, selectedTabId)) {
      return;
    }

    if (!newInput.name && !input.name) {
      const defaultName = newInput?.type ? DEFAULT_NAMES[newInput?.type] : null;
      if (defaultName) {
        newInput.name = defaultName;
      }
    }

    if (isSave) {
      const payload = buildComponentPayloadForSaved(newInput);
      const isCoachingMaster =
        payload?.type === ComponentTypeEnum.OneOneCoachingMaster;

      const introSessionPayload = isCoachingMaster
        ? getIntroSessionPayload(introSessionValue)
        : null;
      let meetingSessionPayload = isCoachingMaster
        ? getMeetingSessionPayload(meetingSessionValue)
        : [];

      if (isCoachingMaster) {
        const isMasterCompletionRequirementChanged =
          programComponent?.optional !== payload.optional;

        if (isMasterCompletionRequirementChanged) {
          meetingSessionPayload =
            meetingSessionPayload?.map((session) => {
              return {
                ...session,
                optional: payload.optional,
              };
            }) ?? [];
        } else {
          payload.optional =
            meetingSessionPayload &&
            meetingSessionPayload.length > 0 &&
            meetingSessionPayload.every((session) => session?.optional);
        }
      }

      saveProgramComponent({
        variables: {
          programId,
          input: payload,
          introSessionInput: introSessionPayload || undefined,
          meetingSessionInput: meetingSessionPayload || undefined,
        },
      });
    } else {
      if (isNext) {
        handleNextTab();
      }
    }
    setInput(newInput);
    setIntroSessionInput(introSessionValue);
    setMeetingSessionInput(meetingSessionValue || []);
  };

  useEffect(() => {
    if (programId && componentId) {
      fetchComponent({
        variables: {
          programId,
          programComponentId: componentId,
        },
      });
    }
    if (programId && input?.type === ComponentTypeEnum.Survey) {
      fetchSurveyTemplates({ variables: { programId } });
    }
  }, [componentId, programId, input?.type]);

  useEffect(() => {
    setSelectedTabId(() => {
      if (componentId && input?.type === ComponentTypeEnum.Survey) {
        return tabList?.[1]?.key;
      }
      if (input?.type && !componentId) {
        return tabList?.[1]?.key;
      }
      return tabList?.[0]?.key;
    });
  }, [componentId, input?.type, tabList?.length]);

  const handleOnAddMore = () => {
    setInput(INITIAL_INPUT_STATE);
    setSelectedTabId(tabList?.[0]?.key);
  };

  const handleOnClose = () => {
    const newType = input?.type;
    const newComponentId =
      input?.id && input?.id !== componentId ? input?.id : undefined;
    const tagID = `#component_id_${newComponentId}`;

    setInput(INITIAL_INPUT_STATE);
    setIntroSessionInput(undefined);
    setMeetingSessionInput([]);

    const isFinalTab =
      tabList?.length > 1 &&
      tabList?.[tabList?.length - 1]?.key === selectedTabId;
    if (isFinalTab) {
      if (redirect) {
        window.location.replace(redirect);
      } else if (redirectByComponentType) {
        const path = getComponentDetailsPaths({
          programId,
          type: newType,
          tagID,
          currentRoute: location.pathname,
        });
        if (path) {
          window.location.replace(path);
        }
      }
    }
    setSelectedTabId(tabList?.[0]?.key);
    onClose?.(newComponentId);
  };

  const currentTabs = tabList?.map((item) => {
    const isCurrentTab = item?.key === selectedTabId && item?.label;
    const isCompletedTab =
      item?.label && (item?.completed || item?.key < selectedTabId);

    const icon = isCurrentTab
      ? ICON_FULL_CIRCLE
      : isCompletedTab
        ? ICON_TICK_CIRCLE
        : null;

    return {
      ...item,
      icon,
    };
  });

  return (
    <StyledDialog
      className="full-width-dialog"
      isOpen={open}
      centered={true}
      lazy={true}
      title={
        <Box fontSize={18} fontWeight={600}>
          {!componentId ? "Add Component" : "Edit Component"}
        </Box>
      }
      width="100%"
      onClose={handleOnClose}
      onOpening={() => {
        setErrorMessage(null);
        setInput({
          ...INITIAL_INPUT_STATE,
          type: componentType,
        });
        setProgramComponent(undefined);
      }}
    >
      <Box css={{ height: "100%" }}>
        <StyledTabs
          animate={true}
          id="TabComponent"
          key={"vertical"}
          renderActiveTabPanelOnly={true}
          vertical
          onChange={handleTabChange}
          selectedTabId={selectedTabId}
        >
          {currentTabs.map(({ key, icon, label, Component }) => (
            <Tab
              id={key}
              key={key}
              icon={icon}
              title={label}
              panel={
                <>
                  {errorMessage && (
                    <Box mt={3} css={ErrorBoxCSS}>
                      <div className="flex flex-col gap-6 max-w-148 my-0 mx-auto p-9">
                        <Callout icon={null} intent={Intent.DANGER}>
                          {errorMessage}
                        </Callout>
                      </div>
                    </Box>
                  )}
                  {loading ? (
                    <LoadingOverlay />
                  ) : (
                    <Component
                      onNext={({
                        value,
                        introSessionValue,
                        meetingSessionValue,
                        isSave = true,
                        isNext = true,
                        skipValidation = false,
                      }: NextInputProps) => {
                        handleOnNext({
                          value,
                          introSessionValue:
                            introSessionValue || introSessionInput,
                          meetingSessionValue:
                            meetingSessionValue || meetingSessionInput,
                          isSave,
                          isNext,
                          skipValidation,
                        });
                      }}
                      input={input}
                      programId={programId}
                      programStatus={programStatus}
                      targetLaunchDate={targetLaunchDate}
                      programComponent={programComponent!}
                      onClose={handleOnClose}
                      onAddMore={handleOnAddMore}
                      saving={saving}
                      isEdit={!!componentId}
                      surveyTemplateData={surveyTemplateData}
                      introSessionInput={introSessionInput}
                      meetingSessionInput={meetingSessionInput}
                    />
                  )}
                </>
              }
            />
          ))}
        </StyledTabs>
      </Box>
    </StyledDialog>
  );
}

export const StyledTabs = styled(Tabs)`
  display: flex;
  height: 100%;

  &.${BP_NS}-tabs.${BP_NS}-vertical {
    .${BP_NS}-tab-list {
      width: 210px;
      padding-top: 20px;
      background: #f6f6fb;
      max-height: calc(100vh - 60px);

      .${BP_NS}-tab {
        padding: 16px 24px;
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 16px;
      }

      @media screen and (max-width: ${breakpoints.sm}px) {
        display: none;
      }
    }
  }
  .${BP_NS}-tab-panel {
    flex: 1;
    padding: 0px 36px 36px 36px;
    overflow-y: scroll;
    max-height: calc(100vh - 60px);
  }
  .${BP_NS}-tab-indicator-wrapper {
    display: none;
  }

  &.hide-tab-panel {
    .${BP_NS}-tab-list {
      display: none;
    }
  }
`;
